/**
 * Interceptor de Suscripción - LocalCenter
 * Maneja errores relacionados con límites y features de suscripción
 */

import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { NotificationService } from '../../services/notification.service';
import { SuscripcionService } from '../../services/suscripcion.service';

// Tipos de errores de suscripción
type SubscriptionErrorCode =
  | 'SUBSCRIPTION_LIMIT_REACHED'
  | 'FEATURE_NOT_AVAILABLE'
  | 'NO_ACTIVE_SUBSCRIPTION'
  | 'SUBSCRIPTION_EXPIRED'
  | 'SUBSCRIPTION_UNPAID';

interface SubscriptionErrorResponse {
  success: false;
  error: SubscriptionErrorCode;
  message: string;
  data?: {
    tipo?: string;
    feature?: string;
    actual?: number;
    limite?: number;
    porcentajeUso?: number;
    requiereUpgrade?: boolean;
    requierePago?: boolean;
    estado?: string;
  };
}

export const subscriptionInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const notificationService = inject(NotificationService);
  const suscripcionService = inject(SuscripcionService);

  return next(request).pipe(
    // Leer headers de suscripción de la respuesta
    tap(event => {
      if (event instanceof HttpResponse) {
        const headers = event.headers;

        // Leer alertas de suscripción de los headers
        const alertCount = headers?.get('X-Subscription-Alerts');
        const trialDays = headers?.get('X-Subscription-Trial-Days');
        const status = headers?.get('X-Subscription-Status');

        // Si hay alertas o el trial está por vencer, mostrar notificación sutil
        if (trialDays && parseInt(trialDays) <= 3 && parseInt(trialDays) > 0) {
          // No mostrar en cada request, solo una vez por sesión
          const lastShown = sessionStorage.getItem('trial_warning_shown');
          if (!lastShown) {
            notificationService.showWarning(
              `Tu período de prueba termina en ${trialDays} días`,
              8000
            );
            sessionStorage.setItem('trial_warning_shown', 'true');
          }
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      // Solo manejar errores 403 relacionados con suscripción
      if (error.status === 403 && error.error?.error) {
        const subscriptionError = error.error as SubscriptionErrorResponse;

        switch (subscriptionError.error) {
          case 'SUBSCRIPTION_LIMIT_REACHED':
            handleLimitReached(notificationService, subscriptionError);
            break;

          case 'FEATURE_NOT_AVAILABLE':
            handleFeatureNotAvailable(notificationService, subscriptionError);
            break;

          case 'NO_ACTIVE_SUBSCRIPTION':
            handleNoSubscription(notificationService);
            break;

          case 'SUBSCRIPTION_EXPIRED':
            handleExpiredSubscription(notificationService, subscriptionError);
            break;

          case 'SUBSCRIPTION_UNPAID':
            handleUnpaidSubscription(notificationService);
            break;
        }

        // Refrescar datos de suscripción
        suscripcionService.refrescar();
      }

      return throwError(() => error);
    })
  );
};

function handleLimitReached(
  notificationService: NotificationService,
  error: SubscriptionErrorResponse
): void {
  const tipo = error.data?.tipo || 'recursos';
  const actual = error.data?.actual;
  const limite = error.data?.limite;

  let mensaje = error.message || `Has alcanzado el límite de ${tipo} de tu plan`;
  if (actual !== undefined && limite !== undefined) {
    mensaje = `Límite alcanzado: ${actual}/${limite} ${tipo}`;
  }

  notificationService.showWarning(mensaje, 8000);
}

function handleFeatureNotAvailable(
  notificationService: NotificationService,
  error: SubscriptionErrorResponse
): void {
  const feature = error.data?.feature;
  const featureNames: Record<string, string> = {
    'afip_facturacion': 'Facturación electrónica AFIP',
    'multi_sucursal': 'Multi-sucursal',
    'reportes_avanzados': 'Reportes avanzados',
    'api_access': 'Acceso a API',
    'soporte_prioritario': 'Soporte prioritario'
  };

  const featureName = feature ? (featureNames[feature] || feature) : 'Esta funcionalidad';

  notificationService.showWarning(
    `${featureName} no está disponible en tu plan actual`,
    8000
  );
}

function handleNoSubscription(notificationService: NotificationService): void {
  notificationService.showWarning(
    'No tienes una suscripción activa. Elige un plan para continuar.'
  );
}

function handleExpiredSubscription(
  notificationService: NotificationService,
  error: SubscriptionErrorResponse
): void {
  const estado = error.data?.estado;
  const mensaje = estado === 'cancelada'
    ? 'Tu suscripción fue cancelada'
    : 'Tu suscripción ha vencido';

  notificationService.showError(
    `${mensaje}. Renueva para seguir usando LocalCenter.`
  );
}

function handleUnpaidSubscription(notificationService: NotificationService): void {
  notificationService.showWarning(
    'Tu suscripción tiene pagos pendientes. Regulariza tu situación para evitar interrupciones.'
  );
}
